import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { MICROSERVICES } from '@shared/constants/microservices';
import { Observable } from 'rxjs/internal/Observable';
import { KeyPairingStateResponse, OemAuthResponse, OemMetadataResponse, PairingUrlResponse, VehicleConfigStateResponse } from '../domain/oem.model';
import { ExchangeCodeDto, OemDto } from './dto/oem.dto';
import { VehicleEligibilityDto } from './dto/vehicle.dto';
import { OemRepository } from './oem.repository';

@Injectable()
export class OemApiService implements OemRepository {
    private _http = inject(HttpClient);

    eligibilityChecks(vehicleEligibilityDto: VehicleEligibilityDto): Observable<void> {
        return this._http.post<void>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/eligibility-checks`, vehicleEligibilityDto);
    }

    auth(authOemDto: OemDto): Observable<OemAuthResponse> {
        return this._http.get<OemAuthResponse>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/${authOemDto.vehicle_id}/auth`);
    }

    metadata(oem: string): Observable<OemMetadataResponse> {
        return this._http.get<OemMetadataResponse>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/config/${oem}/metadata`);
    }

    exchangeCode(vehicleId: string, exchangeCodeDto: ExchangeCodeDto): Observable<void> {
        const hasOAuthParams = !!exchangeCodeDto.state && !!exchangeCodeDto.code;
        const hasBlockchainParams = !!exchangeCodeDto.token && !!exchangeCodeDto.walletAddress;
        if (!hasOAuthParams && !hasBlockchainParams) {
            throw new Error('Exchange code error');
        }
        const body = {
            ...(exchangeCodeDto.state && { state: exchangeCodeDto.state }),
            ...(exchangeCodeDto.code && { code: exchangeCodeDto.code }),
            ...(exchangeCodeDto.token && { token: exchangeCodeDto.token }),
            ...(exchangeCodeDto.walletAddress && { walletAddress: exchangeCodeDto.walletAddress }),
        };
        return this._http.post<void>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/${vehicleId}/exchange-code`, body);
    }

    keyPairingState(vehicleId: string): Observable<KeyPairingStateResponse> {
        return this._http.get<KeyPairingStateResponse>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/tesla/${vehicleId}/key-pairing-state`);
    }

    pairingUrl(vehicleId: string): Observable<PairingUrlResponse> {
        return this._http.get<PairingUrlResponse>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/tesla/${vehicleId}/pairing-url`);
    }

    vehicleConfigState(vehicleId: string): Observable<VehicleConfigStateResponse> {
        return this._http.get<VehicleConfigStateResponse>(
            `${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/tesla/${vehicleId}/vehicle-config-state`
        );
    }

    sendVehicleConfig(vehicleId: string): Observable<void> {
        return this._http.post<void>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/tesla/${vehicleId}/send-vehicle-config`, {});
    }

    smartCarWebhookSubscription(vehicleId: string): Observable<void> {
        return this._http.post<void>(`${environment.url}${MICROSERVICES.oemConnector}/v1/vehicles/smartcar/webhooks/odometer/subscribe/${vehicleId}`, {});
    }
}
